import React, { useEffect, useRef } from 'react';
import { fromJS } from 'immutable';
import classnames from 'classnames';
import './Content.css';
import SideBar from './SideBar';
import ShopView from '../screens/PO/ShopView';
import { Navigate, Route, Routes } from 'react-router-dom';
import * as LoginComponents from '../screens/Home/Login';
import VehicleSearchCreatePO from '../shared/VehicleSearchCreatePO/containers/VehicleSearchCreatePOView';
import AddEditView from '../screens/PO/AddEditView';
import OdometerView from '../screens/PO/OdometerView';
import RepairHistoryView from '../screens/PO/RepairHistoryView';
import ComplaintView from '../screens/PO/ComplaintView';
import ClosePO from '../screens/PO/ClosePO';
import SearchPO from '../screens/PO/SearchPO';
import ClientParameters from '../screens/PO/ClientParameters';
import ActionRequired from '../screens/PO/ActionRequired';
import HelpFAQ from '../screens/Contact/HelpFAQ';
import FeedbackForm from '../screens/Contact/FeedbackForm';
import VendorAccount from '../screens/YourAccount/VendorAccountInformation';
import BulletinMessage from '../screens/PO/ShopView/components/BulletinMessage';
import RecoverPassword from '../screens/Home/RecoverPassword';
import MyUnits from '../screens/YourAccount/MyUnits/containers/MyUnitsView';
import VehicleDocuments from 'Vehicle/VehicleDocuments';
import Adjustment from 'PO/Adjustment';
import DriverValidationComplaints from 'PO/DriverValidationComplaints';
import { connect } from 'react-redux';
import {
  falseIfUndefined,
  isValidToken,
} from '../shared/utils/HelperFunctions';
import VendorEnrollmentRequestView from '../screens/YourAccount/VendorEnrollmentRequest';
import VendorEnrollmentView from '../screens/YourAccount/VendorEnrollment';
import RegistrationView from '../screens/YourAccount/Registration';
import VendorLinkedAccountsView from '../screens/CorporateAccount/containers/LinkedVendorView';
import VendorLinkedRegister from '../screens/CorporateAccount/LinkedVendorRegister/containers/LinkedVendorRegister';
import CarWashView from '../screens/CarWash/containers/CarWashView';
import CarWashCreatePO from '../screens/CarWash/screens/CreatePOForm/containers/CreatePOFormView';
import DealerDraftsDealerRequests from '../screens/DealerDrafts/DealerRequests/containers/DealerRequestsView';
import DealerDraftsRequestsDetails from '../screens/DealerDrafts/RequestsApproval/containers/RequestsApprovalView';
import TermsAndConditions from '../screens/Home/TermsAndConditions/containers/TermsView';
import TermsAndConditionsModal from 'TermsAndConditionsModal';
import ClientExceptions from '../screens/Home/ClientExceptions/containers/ClientExceptionsView';
import SSORedirect from 'SSORedirect';
import VehicleSearchResultView from '../shared/VehicleSearchResults/containers/VehiclesSearchResultsView';
import ResetPassword from '../screens/Home/ResetPassword';
import RecoverPasswordConfirmationComponent from '../screens/Home/RecoverPassword/components/RecoverPasswordConfirmationComponent';
import ResetPasswordResultView from '../screens/Home/ResetPassword/components/ResetPasswordResultView';
import RecoverPasswordTooAttemptsComponent from '../screens/Home/RecoverPassword/components/RecoverPasswordTooAttemptsComponent';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import ARIUser from '../screens/ARIUser';
import ChangePassword from 'YourAccount/ChangePassword';
import TLSLearnMore from 'Home/TLSWarning/components/TLSLearnMore';
import AttentionNeeded from '../screens/AttentionNeeded';

import PersonaRoutes from 'PersonaRoutes/common/Routes';
import { useAppState } from 'shared/AppState/app-state';
import * as Api from 'utils/Api';
import { HolmanRedirect } from '../components/HolmanRedirect';
import IELoginWarningModal from './IELoginWarningModal';
import PaymentStatusBulletin from '../screens/PO/ShopView/components/PaymentStatusBulletin';
import AccidentManagementRoute from 'AccidentManagement/common/Routes.tsx';
import { setContext } from '@apollo/client/link/context';
import { ApolloClient, HttpLink, InMemoryCache, from } from '@apollo/client';
import configuration from 'shared/configuration';
import RepairContactView from 'screens/PO/RepairContactView/RepairContactView.tsx';
import ReviewAccountInfo from 'screens/YourAccount/ReviewAccountInfo/containers/ReviewAccountInfo.tsx';
import useAutoVersionCheck from '../shared/hooks/useAutoVersionCheck';
import SessionExpiration from '../components/SessionExpiration';
import { isPersonaRoute, syncPostHogAnalytics } from '../shared/utils/PostHogAnalytics';

const Content = ({
  dispatch: reduxDispatch,
  token,
  personaToken,
  vendorId,
  personaUser,
  serviceClassification,
  isDealerDraft,
  adminUser,
  selectedLocale,
  country,
  location,
}) => {
  const [appState, dispatch] = useAppState();
  const isIE = false || !!document.documentMode;
  const hasValidToken = isValidToken(token);
  const hasValidPersonaToken = isValidToken(personaToken);
  const hasAuthenticatedSession = hasValidToken || hasValidPersonaToken;
  const isPersonaPage = isPersonaRoute(location.pathname);
  const personaEntry = useRef({ isPersonaPage: false, token: undefined });
  const authenticatedSideBar = hasAuthenticatedSession ? <SideBar /> : null;

  // Apollo GraphQL client for vendor service API
  // TODO: Remove this and replace with a generic Apollo client for all API calls for Federated GraphQL
  const authLink = setContext((_, { headers }) => {
    // get the authentication token from local storage if it exists
    const token = appState.token;
    const requestHeaders = { ...(headers || {}) };
    delete requestHeaders.authorization;
    // return the headers to the context so httpLink can read them
    return {
      headers: {
        ...requestHeaders,
        ...(isValidToken(token) ? { authorization: `Bearer ${token}` } : {}),
        'graphql-client-name': configuration.graphQLClientName,
        'graphql-client-version': configuration.graphQLClientVersion,
      },
    };
  });
  const vendorHttpLink = new HttpLink({
    uri: (appState.isLegacyGraphqlRouterEnabled ? configuration.vendorServiceUrl : configuration.fleetVendorRouterUrl) + 'graphql/',
  });
  const vendorClient = new ApolloClient({
    cache: new InMemoryCache(),
    link: from([authLink, vendorHttpLink]),
  });

  useAutoVersionCheck(!!appState.isDeploymentVersionValidCheck);

  useEffect(() => {
    dispatch({ type: 'TOKEN_CHANGED', token });
  }, [dispatch, token]);

  useEffect(() => {
    syncPostHogAnalytics({
      configuration,
      token,
      personaToken,
      pathname: location.pathname,
      isValidToken,
    });
  }, [token, personaToken, location.pathname]);

  // Save vendor features in AppState
  useEffect(() => {
    const fetchVendorFeatures = async () => {
      if (hasValidToken && vendorId !== undefined) {
        try {
          // Make API call
          const vendorFeatures = await Api.fetchVendorFeatures(vendorId, token);

          // Save features
          dispatch({ type: 'VENDOR_FEATURES_LOADED', vendorFeatures });
          reduxDispatch({ type: 'VENDOR_FEATURES_LOADED', vendorFeatures });

          // Save tax limit to redux. Needed for validation selector
          const overrideTaxLimit = vendorFeatures.includes('override_tax_threshold_limit');
          reduxDispatch({ type: 'SAVE_OVERRIDE_TAX_LIMIT', overrideTaxLimit });
        } catch (e) {
          // Expired-session cleanup is handled centrally; do not keep retrying this optional preload.
        }
      }
    };
    fetchVendorFeatures();
  }, [dispatch, token, vendorId, hasValidToken]);

  useEffect(() => {
    dispatch({ type: 'PERSONA_TOKEN_CHANGED', token: personaToken });
  }, [dispatch, personaToken]);

  useEffect(() => {
    let personaUserJSON = personaUser === undefined ? undefined : personaUser.toJSON();
    dispatch({ type: 'PERSONA_USER_CHANGED', personaUser: personaUserJSON });
  }, [dispatch, personaUser]);

  useEffect(() => {
    dispatch({ type: 'VENDOR_TYPE_CHANGED', serviceClassification: serviceClassification });
  }, [serviceClassification]);

  useEffect(() => {
    dispatch({ type: 'VENDOR_IS_DEALER', dealerDraftStatus: isDealerDraft });
  }, [isDealerDraft]);

  // Only clear the Redux store token present on entry to the Persona area. On reload,
  // wait for TOKEN_CHANGED to synchronize local state; tokens selected afterward survive.
  useEffect(() => {
    if (personaEntry.current.isPersonaPage !== isPersonaPage) {
      personaEntry.current = { isPersonaPage, token: isPersonaPage ? token : undefined };
    }
    const entry = personaEntry.current;
    if (entry.token && token !== entry.token) entry.token = undefined;
    if (isPersonaPage && entry.token && appState.token === entry.token) {
      entry.token = undefined;
      reduxDispatch({ type: 'PERSONA_USER_STORE_SIGN_OUT' });
      reduxDispatch({
        type: 'SET_PERSONA_DETAILS',
        personaToken: appState.personaToken,
        personaUserId: appState.personaUserId,
        adminUser: adminUser,
        selectedLocale: selectedLocale,
        personaUser: fromJS(appState.personaUser),
      });
    }
  }, [isPersonaPage, token, appState, reduxDispatch, adminUser, selectedLocale]);

  const vehicleSearchCreateComponent =
    country == 'MEX' ||
    !appState.poPermissions.includes('CR') ||
    (appState.serviceClassification === 'CARWASH' && !appState.poPermissions.includes('CL')) ? (
      <div></div>
    ) : (
      <VehicleSearchCreatePO />
    );

  return (
    <div className="row container-fluid">
      <HolmanRedirect />
      {(appState.isUserInactiveCheck || appState.personaToken) && <SessionExpiration />}
      <ChangePassword />
      <TermsAndConditionsModal location={location} />
      <IELoginWarningModal />
      <div className="d-none d-xl-block links offset-lg-1 col-lg-1 offset-xl-1 col-xl-2">
        <Routes>
          <Route path="/vendorTermsAndConditions" element={<div></div>} />
          <Route path="/personaTermsAndConditions" element={<div></div>} />
          <Route path="/sso" element={<div></div>} />
          <Route path="/login" element={<div></div>} />
          <Route path="/Persona/*" element={authenticatedSideBar} />
          <Route path="/attentionNeeded" element={authenticatedSideBar} />
          <Route path="/AccidentManagement/*" element={authenticatedSideBar} />
          <Route path="/" element={authenticatedSideBar} />
          <Route path="/reviewAccountInformation" element={authenticatedSideBar} />
          <Route path="/contact/HelpFAQ" element={authenticatedSideBar} />
          <Route path="/contact/FeedbackForm" element={authenticatedSideBar} />
          <Route path="/yourAccount" element={authenticatedSideBar} />
          <Route path="/Stores" element={authenticatedSideBar} />
          <Route path="/StoreProfile/:storeId" element={authenticatedSideBar} />
          <Route path="/ariuser" element={<div></div>} />
          <Route
            path="/linkedVendorAccount"
            element={
              !hasValidToken ? (
                <Navigate to="/login" replace />
              ) : null
            }
          />
          <Route path="/resetPassword" element={null} />
          <Route path="/recoverPassword" element={null} />
          <Route path="/terms" element={null} />
          <Route path="/registration/:step" element={<div></div>} />
          <Route path="/vehicle/:vehicleId/documents" element={authenticatedSideBar} />
          <Route path="/carWash" element={authenticatedSideBar} />
          <Route path="/carWash/vehicle/:vehicleId" element={authenticatedSideBar} />
          <Route path="/dealerDrafts" element={authenticatedSideBar} />
          <Route path="/dealerDrafts/:actionType/:requestNo" element={authenticatedSideBar} />
          <Route path="/learnMore/:id" element={authenticatedSideBar} />
          <Route path="/po/search" element={authenticatedSideBar} />

          <Route path="/vehicles/vin8/:vin8/:actionType" element={authenticatedSideBar} />
          <Route path="/vehicles/plate/:plate/state/:state/:actionType" element={authenticatedSideBar} />
          <Route path="/vehicles/clientId/:clientId/unitNo/:unitNo/:actionType" element={authenticatedSideBar} />
          <Route path="/vehicles/serviceNo/:serviceNo/:actionType" element={authenticatedSideBar} />

          <Route path="/po/odometer/:vehicleId" element={authenticatedSideBar} />
          <Route path="/po/repairHistory/:vehicleID" element={authenticatedSideBar} />
          <Route path="/po/driverValidationComplaints/:vehicleId" element={authenticatedSideBar} />
          <Route path="/po/complaint/:vehicleId" element={authenticatedSideBar} />
          <Route path="/po/close/:trackNumber" element={authenticatedSideBar} />
          <Route path="/po/clientParameters/:vehicleID" element={authenticatedSideBar} />
          <Route path="/po/actionRequired/:trackNumber" element={authenticatedSideBar} />
          <Route path="/po/adjustment/:poNumber" element={authenticatedSideBar} />
          <Route path="/po/contact/:vehicleId" element={authenticatedSideBar} />
          <Route path="/po/:actionType/:trackNumber" element={authenticatedSideBar} />
          <Route path="/tlsWarning/learnMore" element={authenticatedSideBar} />
          <Route path="/paymentStatusBulletin/learnMore" element={authenticatedSideBar} />
        </Routes>
      </div>
      <div
        className={
          isIE
            ? classnames(
                'col-sm-12',
                'offset-md-1',
                'col-md-11',
                'offset-lg-2',
                'col-lg-8',
                'offset-xl-0'
              )
            : classnames(
                'col-sm-12',
                'offset-md-1',
                'col-md-11',
                'offset-lg-2',
                'col-lg-8',
                'offset-xl-0',
                'col-xl-8'
              )
        }
      >
        <Routes>
          <Route
            path="/exceptions"
            element={
              <div className="data-content">
                <ClientExceptions />
              </div>
            }
          />
        <Route
          path="/sso"
          element={
            <div className="data-content">
              <SSORedirect />
            </div>
          }
        />
        <Route path="/login" element={<LoginComponents.LoginContent />} />
        <Route
          path="/"
          element={
            !hasAuthenticatedSession ? (
              <Navigate to="/login" />
            ) : (
              <div className="data-content">
                {vehicleSearchCreateComponent}
                <ShopView />{' '}
              </div>
            )
          }
        />
        <Route
          path="/reviewAccountInformation"
          element={
            <div className="data-content">
              <ReviewAccountInfo vendorId={vendorId} vendorClient={vendorClient} />{' '}
            </div>
          }
        />
        <Route
          path="/contact/HelpFAQ"
          element={
            serviceClassification === 'CARWASH' || !hasValidToken ? (
              <div className="data-content">
                <HelpFAQ />
              </div>
            ) : (
              <div className="data-content">
                {vehicleSearchCreateComponent}
                <HelpFAQ />
              </div>
            )
          }
        />
        <Route
          path="/contact/FeedbackForm"
          element={
            serviceClassification === 'CARWASH' || !hasValidToken ? (
              <div className="data-content">
                <FeedbackForm />
              </div>
            ) : (
              <div className="data-content">
                {vehicleSearchCreateComponent}
                <FeedbackForm />
              </div>
            )
          }
        />
        <Route
          path="/yourAccount"
          element={
            serviceClassification === 'CARWASH' ? (
              <div className="data-content">
                <VendorAccount
                  vendorClient={vendorClient}
                  isPaymentStatusCheckEnabled={appState.isPaymentStatusCheckEnabled}
                />
              </div>
            ) : (
              <div className="data-content">
                {vehicleSearchCreateComponent}{' '}
                <VendorAccount
                  vendorClient={vendorClient}
                  isPaymentStatusCheckEnabled={appState.isPaymentStatusCheckEnabled}
                />
              </div>
            )
          }
        />
        <Route
          path="/ariuser"
          element={
            <div className="data-content ari-user-screen">
              <ARIUser />
            </div>
          }
        />
        <Route
          path="/carWash"
          element={
            <div className="data-content">
              {vehicleSearchCreateComponent}
              <CarWashView />
            </div>
          }
        />
        <Route
          path="/carWash/vehicle/:vehicleId"
          element={
            <div className="data-content">
              {vehicleSearchCreateComponent}
              <CarWashCreatePO />{' '}
              <CarWashView />
            </div>
          }
        />

        <Route
          path="/linkedVendorAccount"
          element={
            <div className="data-content">
              <VendorLinkedAccountsView />
            </div>
          }
        />
        <Route
          path="/linkedVendorAccount/Register"
          element={
            <div className="data-content">
              <VendorLinkedRegister />
            </div>
          }
        />
        <Route
          path="/recoverPassword"
          element={
            <div className="data-content">
              <RecoverPassword />
            </div>
          }
        />
        <Route
          path="/resetPassword"
          element={
            <div className="data-content">
              <ResetPassword />
            </div>
          }
        />
        <Route
          path="/resetPassword/confirmation/:status"
          element={
            <div className="data-content">
              <ResetPasswordResultView />
            </div>
          }
        />
        <Route
          path="/recoverPassword/confirmation/:status"
          element={
            <div className="data-content">
              <RecoverPasswordConfirmationComponent />
            </div>
          }
        />
        <Route
          path="/recoverPassword/tooManyAttempts"
          element={
            <div className="data-content">
              <RecoverPasswordTooAttemptsComponent />
            </div>
          }
        />
        <Route
          path="/yourAccount/myUnits"
          element={
            <div className="data-content">
              {vehicleSearchCreateComponent}
              <MyUnits />
            </div>
          }
        />
        <Route
          path="/terms"
          element={
            <div className="data-content">
              <TermsAndConditions />
            </div>
          }
        />

        <Route
          path="/vehicle/:vehicleId/documents"
          element={
            <div className="data-content">
              <VehicleDocuments />
            </div>
          }
        />

        <Route
          path="/learnMore/:id"
          element={
            !hasAuthenticatedSession ? (
              <div className="data-content">
                <BulletinMessage />
              </div>
            ) : (
              <div className="data-content">
                {vehicleSearchCreateComponent}
                <BulletinMessage />
              </div>
            )
          }
        />

        <Route
          path="/po/search"
          element={
            <div className="data-content">
              {vehicleSearchCreateComponent}
              <SearchPO />
            </div>
          }
        />

        <Route
          path="/vehicles/vin8/:vin8/:actionType"
          element={
            <div className="data-content">
              {vehicleSearchCreateComponent}
              <VehicleSearchResultView />
            </div>
          }
        />
        <Route
          path="/vehicles/plate/:plate/state/:state/:actionType"
          element={
            <div className="data-content">
              {vehicleSearchCreateComponent}
              <VehicleSearchResultView />
            </div>
          }
        />
        <Route
          path="/vehicles/clientId/:clientId/unitNo/:unitNo/:actionType"
          element={
            <div className="data-content">
              {vehicleSearchCreateComponent}
              <VehicleSearchResultView />
            </div>
          }
        />
        <Route
          path="/vehicles/serviceNo/:serviceNo/:actionType"
          element={
            <div className="data-content">
              {vehicleSearchCreateComponent}
              <VehicleSearchResultView />
            </div>
          }
        />

        <Route
          path="/persona/setupPassword"
          element={
            <div className="data-content">
              <ResetPassword />
            </div>
          }
        />
        <Route
          path="/persona/resetPassword"
          element={
            <div className="data-content">
              <ResetPassword />
            </div>
          }
        />
        <Route
          path="/persona/resetPassword/confirmation/:status"
          element={
            <div className="data-content">
              <ResetPasswordResultView />
            </div>
          }
        />
        <Route
          path="/Persona/*"
          element={
            <div className="data-content">
              <PersonaRoutes />
            </div>
          }
        />
        <Route
          path="/AccidentManagement/*"
          element={
            <div className="data-content">
              <AccidentManagementRoute
                isAccidentAssignmentFeatureEnabled={appState.isAccidentAssignmentFeatureEnabled}
                vendorId={vendorId}
              />
            </div>
          }
        />

        <Route
          path="/po/repairHistory/:vehicleID"
          element={
            <div className="data-content">
              {vehicleSearchCreateComponent}
              <RepairHistoryView />
            </div>
          }
        />
        <Route
          path="/po/odometer/:vehicleId"
          element={
            <div className="data-content">
              {vehicleSearchCreateComponent}
              <OdometerView />
            </div>
          }
        />
        <Route
          path="/po/contact/:vehicleId"
          element={
            <div className="data-content">
              {vehicleSearchCreateComponent}
              <RepairContactView
                vendorClient={vendorClient}
                vendorId={vendorId}
                dispatch={dispatch}
                userLocale={selectedLocale}
              />
            </div>
          }
        />
        <Route
          path="/po/driverValidationComplaints/:vehicleId"
          element={
            <div className="data-content">
              {vehicleSearchCreateComponent}
              <DriverValidationComplaints />
            </div>
          }
        />
        <Route
          path="/po/complaint/:vehicleId"
          element={
            <div className="data-content">
              {vehicleSearchCreateComponent}
              <ComplaintView />
            </div>
          }
        />
        <Route
          path="/po/close/:trackNumber"
          element={
            <div className="data-content">
              {vehicleSearchCreateComponent}
              <ClosePO />
            </div>
          }
        />
        <Route
          path="/po/clientParameters/:vehicleID"
          element={
            <div className="data-content">
              {vehicleSearchCreateComponent}
              <ClientParameters />
            </div>
          }
        />
        <Route
          path="/po/actionRequired/:trackNumber"
          element={
            <div className="data-content">
              {vehicleSearchCreateComponent}
              <ActionRequired />
            </div>
          }
        />
        <Route
          path="/po/adjustment/:poNumber"
          element={
            <div className="data-content">
              {vehicleSearchCreateComponent}
              <Adjustment />
            </div>
          }
        />
        <Route
          path="/po/:actionType/:trackNumber"
          element={
            <div className="data-content">
              {vehicleSearchCreateComponent}
              <AddEditView />
            </div>
          }
        />
        <Route
          path="/attentionNeeded"
          element={
            <div className="data-content">
              {vehicleSearchCreateComponent}
              <AttentionNeeded />
            </div>
          }
        />

        <Route
          path="/dealerDrafts"
          element={
            <div className="data-content">
              {' '}
              <DealerDraftsDealerRequests />
            </div>
          }
        />
        <Route
          path="/dealerDrafts/:actionType/:requestNo"
          element={
            <div className="data-content">
              {vehicleSearchCreateComponent}
              <DealerDraftsRequestsDetails />
            </div>
          }
        />

        <Route
          path="/vendorEnrollment/request"
          element={(() => {
            const searchParams = new URLSearchParams(
              typeof window !== 'undefined' ? window.location.search : ''
            );
            const hasToken = !!searchParams.get('token');

            if (configuration.singleUseEnrollmentCodeEnabled && !hasToken) {
              return <Navigate to="/login" replace />;
            }

            return (
              <div className="data-content">
                <VendorEnrollmentRequestView />
              </div>
            );
          })()}
        />

        <Route
          path="/tlsWarning/learnMore"
          element={
            <div className="data-content tls_learn_more_screen">
              <TLSLearnMore />
            </div>
          }
        />
        <Route
          path="/PaymentStatusBulletin/learnMore"
          element={
            <div className="data-content">
              <PaymentStatusBulletin />
            </div>
          }
        />
        <Route
          path="/registration/:step"
          element={
            <div className="container">
              <div className="row justify-center">
                <div className="col-md-2" />
                <div className="col-md-8 data-content vendor-registration__screen">
                  <RegistrationView
                    vendorClient={vendorClient}
                    isVendorPreferredContactEnabled={appState.isVendorPreferredContactEnabled}
                  />
                </div>
                <div className="col-md-2" />
              </div>
            </div>
          }
        />
        </Routes>
      </div>

      <Routes>
        <Route
          path="/vendorEnrollment/request/detail/:enrollId"
          element={
            <div className="vendor-enrollment__screen">
              <div className="row">
                <div className="col-md-2" />
                <div className="col-md-8 data-content">
                  <VendorEnrollmentView />
                </div>
                <div className="col-md-2" />
              </div>
            </div>
          }
        />
      </Routes>
    </div>
  );
};

function mapStateToProps(state) {
  return {
    token: state.appState.getIn(['serverData', 'shared', 'token']),
    personaToken: state.appState.getIn(['serverData', 'shared', 'personaToken']),
    vendorId: state.appState.getIn(['serverData', 'shared', 'vendorId']),
    personaUser: state.appState.getIn(['serverData', 'shared', 'personaUser']),
    serviceClassification: state.appState.getIn(['serverData', 'shared', 'serviceClassification']),
    country: state.appState.getIn(['uiData', 'shared', 'selectedCountry']),
    isDealerDraft: falseIfUndefined(
      state.appState.getIn(['serverData', 'shared', 'isDealerDraft'])
    ),
    adminUser: state.appState.getIn(['serverData', 'shared', 'adminUser']),
    selectedLocale: state.appState.getIn(['uiData', 'shared', 'selectedLocale']),
  };
}

export default connect(mapStateToProps)(Content);
